<script lang="tsx" setup>
import type { KirbyBlock } from "@/queries/blocks"

import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue"
import { Swiper, SwiperSlide } from "swiper/vue"
import { EffectCreative, Keyboard } from "swiper/modules"
import type { Swiper as SwiperInstance } from "swiper"
import {
  useSwiperSpring,
  SPRING_DURATION_MS,
} from "../../composables/use-swiper-spring"
import { useSpring } from "../../composables/use-spring"

const bouncySpring = useSpring("bouncy").config

import "swiper/css"
import "swiper/css/effect-creative"

import { convertBlocksToSlideShowItems } from "../../composables/lightbox"
import { useZoom } from "@/composables/use-zoom"
import type { KirbyImageData } from "@/queries/image"
import { useStore } from "../../store"
import Slideshow_Figure from "../Slideshow/Slideshow_Figure.vue"
import SlideshowVideoPlayer from "../Slideshow/Slideshow_VideoPlayer.vue"
import Slideshow_Sketchfab from "./Slideshow_Sketchfab.vue"
import SlideShowNavigation from "./SlideShowNavigation.vue"
import SVG_Arrow_2 from "../SVGs/SVG_Arrow_2.vue"
import SVG_ZoomIn from "../SVGs/SVG_ZoomIn.vue"
import SVG_Play from "../SVGs/SVG_Play.vue"

const props = withDefaults(
  defineProps<{
    blocks: KirbyBlock[]
    initialSlide?: number
  }>(),
  { initialSlide: 0 },
)

const emit = defineEmits<{
  captionChange: [caption: string]
  slideChange: [index: number]
  loaded: []
  heightChange: [height: number]
}>()

const store = useStore()

const items = computed(() => convertBlocksToSlideShowItems(props.blocks))
const filteredItems = computed(() => items.value.filter(item => item.data))
const loop = computed(() => filteredItems.value.length > 1)

const sideCount = computed(() => Math.max(1, Math.floor((3 - 1) / 2)))

const loopAdditionalSlides = computed(() => {
  const n = filteredItems.value.length
  if (n <= 1) return 0
  return sideCount.value * 2
})

// Swiper's loop needs `slidesPerView + slidesPerGroup + loopAdditionalSlides`
// real slides, otherwise it silently breaks (reorders/reuses DOM nodes, showing
// the wrong image per slide). With few blocks we tile the items so there are
// always enough, then map Swiper's realIndex back to the original block index
// for captions / active state / emitted events.
const minSlidesForLoop = computed(() => 1 + 1 + loopAdditionalSlides.value)
const repeat = computed(() => {
  const n = filteredItems.value.length
  return n > 1 ? Math.max(1, Math.ceil(minSlidesForLoop.value / n)) : 1
})
const displayItems = computed(() =>
  Array.from({ length: repeat.value }).flatMap((_, copy) =>
    filteredItems.value.map((item, origIndex) => ({
      ...item,
      origIndex,
      key: `${item.id}-${copy}`,
    })),
  ),
)

const swiperInstance = ref<SwiperInstance>()
useSwiperSpring(swiperInstance)

type SlideShowNavigationInstance = InstanceType<typeof SlideShowNavigation>
const prevNavRef = ref<SlideShowNavigationInstance | null>(null)
const nextNavRef = ref<SlideShowNavigationInstance | null>(null)

const onSwiper = (swiper: SwiperInstance) => {
  swiperInstance.value = swiper
  if (loop.value) {
    // Nudge Swiper once at speed 0 before settling on the target slide. Without
    // this, the creative-effect transforms on the side slides (esp. the
    // previous one) sometimes fail to render until the first transition. The
    // speed-0 move happens before first paint, so there's no visible flicker.
    swiper.slidePrev(0)
    swiper.slideToLoop(props.initialSlide, 0)
  } else {
    swiper.slideTo(props.initialSlide, 0)
  }
}

// `activeDisplayIndex` is Swiper's realIndex in tiled display-space;
// `activeOrigIndex` maps it back to the original block index.
const activeDisplayIndex = ref(props.initialSlide)
const activeOrigIndex = computed(
  () => displayItems.value[activeDisplayIndex.value]?.origIndex ?? 0,
)

watch(
  () => props.initialSlide,
  newIndex => {
    if (!swiperInstance.value || newIndex === activeOrigIndex.value) return
    if (loop.value) {
      swiperInstance.value.slideToLoop(newIndex)
    } else {
      swiperInstance.value.slideTo(newIndex)
    }
  },
)

const _n = displayItems.value.length
const _initialNeighbors =
  _n > 1
    ? Array.from({ length: sideCount.value }, (_, i) => i + 1).flatMap(o => [
        o % _n,
        (_n - o) % _n,
      ])
    : []
const loadedIndices = ref<Set<number>>(new Set([0, ..._initialNeighbors]))

// Reveal-readiness: the initially-visible slides (active centre + immediate
// neighbours), images only — videos/sketchfab show thumbnails immediately.
const requiredImageIndices = [0, ..._initialNeighbors].filter(
  i => displayItems.value[i]?.type === "image",
)
const loadedImageIndices = new Set<number>()
let hasEmittedLoaded = false
let loadedTimer: ReturnType<typeof setTimeout> | null = null

const emitLoaded = () => {
  if (hasEmittedLoaded) return
  hasEmittedLoaded = true
  if (loadedTimer) {
    clearTimeout(loadedTimer)
    loadedTimer = null
  }
  emit("loaded")
}

const onFigureLoad = (index: number) => {
  loadedImageIndices.add(index)
  if (requiredImageIndices.every(i => loadedImageIndices.has(i))) {
    emitLoaded()
  }
}

// Forward the active slide's rendered media height so the gallery can hug it
// with the caption. All slide types (image/video/sketchfab) report their height.
const onFigureHeight = (index: number, height: number) => {
  if (index === activeDisplayIndex.value) emit("heightChange", height)
}

onMounted(() => {
  // Nothing to wait for (no images, or empty gallery) — reveal immediately.
  if (requiredImageIndices.length === 0) {
    emitLoaded()
    return
  }
  // Safety net: reveal anyway if an image is slow or fails to load.
  loadedTimer = setTimeout(emitLoaded, 4000)
})

onBeforeUnmount(() => {
  if (loadedTimer) clearTimeout(loadedTimer)
})

const activeCaption = computed(() => {
  const item = filteredItems.value[activeOrigIndex.value]
  const data = item?.data as { caption?: string } | null
  return data?.caption ?? ""
})

watch(activeCaption, caption => emit("captionChange", `${caption}`), {
  immediate: true,
})

const onSlideChange = (swiper: SwiperInstance) => {
  const ri = swiper.realIndex
  const total = displayItems.value.length
  activeDisplayIndex.value = ri
  emit("slideChange", activeOrigIndex.value)
  window.dispatchEvent(new CustomEvent("slideshow-slide-change"))
  const neighborOffsets = Array.from(
    { length: sideCount.value },
    (_, i) => i + 1,
  )
  loadedIndices.value = new Set([
    ...loadedIndices.value,
    ri,
    ...neighborOffsets.flatMap(o => [
      (ri + o) % total,
      (ri - o + total) % total,
    ]),
  ])
}

const beforeSlideChangeStart = () => {
  store.dispatch("stopAllVideos")
}

const onSlideNextTransitionStart = () => {
  nextNavRef.value?.trigger()
}

const onSlidePrevTransitionStart = () => {
  prevNavRef.value?.trigger()
}

const slideNext = () => swiperInstance.value?.slideNext()
const slidePrev = () => swiperInstance.value?.slidePrev()

const { openZoom } = useZoom()

// Custom cursor
const containerRef = ref<HTMLElement | null>(null)
const cursorX = ref(0)
const cursorY = ref(0)
const isCursorVisible = ref(false)
// Which kind of media (if any) the pointer is currently over — drives both the
// custom cursor icon and whether a click navigates.
const overContent = ref<null | "image" | "video" | "sketchfab">(null)

const contentTypeAt = (
  target: EventTarget | null,
): null | "image" | "video" | "sketchfab" => {
  const el = (target as Element | null)?.closest("[data-slide-content]")
  return (
    (el?.getAttribute("data-slide-content") as
      | "image"
      | "video"
      | "sketchfab") ?? null
  )
}

const ARROW_W = 80
const ARROW_H = 26 // 80 * (24/74) viewBox ratio

const isLeft = computed(() => {
  if (!containerRef.value) return false
  return cursorX.value < containerRef.value.clientWidth / 2
})

const onCursorMove = (e: MouseEvent) => {
  if (!containerRef.value) return
  const rect = containerRef.value.getBoundingClientRect()
  cursorX.value = Math.max(
    ARROW_W / 2,
    Math.min(e.clientX - rect.left, rect.width - ARROW_W / 2),
  )
  cursorY.value = Math.max(
    ARROW_H / 2,
    Math.min(e.clientY - rect.top, rect.height - ARROW_H / 2),
  )
  overContent.value = contentTypeAt(e.target)
}

const onCursorEnter = () => {
  isCursorVisible.value = true
}
const onCursorLeave = () => {
  isCursorVisible.value = false
  overContent.value = null
}

let pointerDownX = 0
const onPointerDown = (e: PointerEvent) => {
  pointerDownX = e.clientX
}
const onPointerUp = (e: PointerEvent) => {
  if (e.pointerType === "touch") return
  if (filteredItems.value.length <= 1) return
  if (Math.abs(e.clientX - pointerDownX) > 5) return
  // Clicks on the active media zoom (image) or play (video) — never navigate.
  if (contentTypeAt(e.target)) return
  if (!containerRef.value) return
  const rect = containerRef.value.getBoundingClientRect()
  if (e.clientX - rect.left < rect.width / 2) slidePrev()
  else slideNext()
}

// Zoom whichever slide is currently active. Reads Swiper's real active slide
// element (correct even with the loop clones) for the rendered <img> the flight
// animation needs; the image data comes from the original block index.
const zoomActiveSlide = () => {
  const item = filteredItems.value[activeOrigIndex.value]
  if (!item || item.type !== "image" || !item.data) return
  const swiper = swiperInstance.value
  const slideEl = swiper?.slides?.[swiper.activeIndex] as
    | HTMLElement
    | undefined
  const img = slideEl?.querySelector<HTMLImageElement>("img[data-zoom-id]")
  if (!img) return
  openZoom(item.data as KirbyImageData, img.getBoundingClientRect(), img)
}

// Single-widget keyboard model: Swiper's keyboard module owns the arrows; Enter
// zooms the active slide.
const onKeydown = (e: KeyboardEvent) => {
  if (e.key === "Enter") {
    e.preventDefault()
    zoomActiveSlide()
  }
}

defineExpose({ slideNext, slidePrev })

/*
  sideCount = (visibleSlides − 1) / 2. The outermost side slide (at progress ±sideCount)
  always lands at the gutter midpoint (1/12 from edge = −62.5% of slide width from centre).
  Swiper interpolates linearly, so prev/next defines the transform at progress ±1 and
  all other positions follow. Scale staggers automatically: outermost is always 0.25.
*/
const creativeEffect = computed(() => {
  const n = sideCount.value
  const pct = -(62.5 / n)
  const px = 16 / n
  const scale = 1 - 0.75 / n
  return {
    limitProgress: n + 1,
    prev: { translate: [`calc(${pct}% - ${px}px)`, 0, 0], scale, opacity: 1 },
    next: { translate: [`calc(${-pct}% + ${px}px)`, 0, 0], scale, opacity: 1 },
  }
})
</script>

<template>
  <div
    ref="containerRef"
    class="relative w-full h-full overflow-x-hidden"
    :class="{
      'md:cursor-none multi':
        filteredItems.length > 1 &&
        overContent !== 'video' &&
        overContent !== 'sketchfab',
      multi:
        filteredItems.length > 1 &&
        (overContent === 'video' || overContent === 'sketchfab'),
    }"
    @mousemove="onCursorMove"
    @mouseenter="onCursorEnter"
    @mouseleave="onCursorLeave"
    @pointerdown="onPointerDown"
    @pointerup="onPointerUp"
  >
    <!-- Gradients -->
    <div
      class="absolute w-2/12 h-full hidden md:block pointer-events-none z-10"
      v-for="side in ['left-0', 'right-0']"
      :class="side"
      :style="
        side === 'left-0'
          ? 'background: linear-gradient(to right, #9C883A, transparent)'
          : 'background: linear-gradient(to left, #9C883A, transparent)'
      "
    ></div>

    <div
      class="px-4 py-3 slide-strip relative focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
      :tabindex="filteredItems.length > 0 ? 0 : -1"
      role="group"
      aria-roledescription="carousel"
      aria-label="Project images. Use arrow keys to change slide, Enter to zoom."
      @keydown="onKeydown"
    >
      <Swiper
        v-if="filteredItems.length > 0"
        :key="displayItems.length"
        class="w-full h-full"
        :slides-per-view="1"
        :loop="loop"
        :grabCursor="false"
        :loop-additional-slides="loopAdditionalSlides"
        :initial-slide="initialSlide"
        :no-swiping-selector="'iframe'"
        :effect="'creative'"
        :creative-effect="creativeEffect"
        :speed="SPRING_DURATION_MS"
        :keyboard="true"
        :simulate-touch="false"
        :observer="true"
        :observe-slide-children="true"
        :modules="[EffectCreative, Keyboard]"
        @swiper="onSwiper"
        @beforeTransitionStart="beforeSlideChangeStart"
        @slideChange="onSlideChange"
        @slideNextTransitionStart="onSlideNextTransitionStart"
        @slidePrevTransitionStart="onSlidePrevTransitionStart"
      >
        <SwiperSlide v-for="(item, index) in displayItems" :key="item.key">
          <Slideshow_Figure
            v-if="item.type === 'image'"
            :data="item.data"
            :is-active="index === activeDisplayIndex"
            :is-visible="loadedIndices.has(index)"
            :key="item.key"
            @load="onFigureLoad(index)"
            @height-change="onFigureHeight(index, $event)"
          />
          <SlideshowVideoPlayer
            v-if="item.type === 'video'"
            :data="item.data"
            :is-visible="loadedIndices.has(index)"
            :is-active="index === activeDisplayIndex"
            :key="item.key"
            @height-change="onFigureHeight(index, $event)"
          />
          <Slideshow_Sketchfab
            v-if="item.type === 'sketchfab'"
            :data="item.data"
            :is-visible="loadedIndices.has(index)"
            :is-active="index === activeDisplayIndex"
            :key="item.key"
            @height-change="onFigureHeight(index, $event)"
          />
        </SwiperSlide>
      </Swiper>
    </div>

    <div
      v-if="
        isCursorVisible &&
        filteredItems.length > 1 &&
        overContent !== 'video' &&
        overContent !== 'sketchfab'
      "
      class="hidden md:block absolute z-30 pointer-events-none"
      :style="{ left: `${cursorX}px`, top: `${cursorY}px` }"
    >
      <!-- Over gutters / empty strip: directional navigation arrow -->
      <div
        v-if="!overContent"
        class="w-16 text-white stroke-black"
        :style="{
          transform: `translate(-50%, -50%) scaleX(${isLeft ? -1 : 1})`,
        }"
      >
        <SVG_Arrow_2 />
      </div>
      <!-- Over the active media: zoom (image) or play (video) affordance -->
      <!-- <div
        v-else
        class="flex items-center justify-center w-12 h-12 p-1.5 bg-white border text-black"
        :style="{ transform: 'translate(-50%, -50%)' }"
      >
        <SVG_ZoomIn v-if="overContent === 'image'" />
        <SVG_Play v-else />
      </div> -->
    </div>

    <!-- <SlideShowNavigation
      ref="prevNavRef"
      v-if="filteredItems.length > 1"
      direction="prev"
      :spring="bouncySpring"
      @click="slidePrev"
    />

    <SlideShowNavigation
      ref="nextNavRef"
      v-if="filteredItems.length > 1"
      direction="next"
      :spring="bouncySpring"
      @click="slideNext"
    /> -->
  </div>
</template>

<style scoped>
.slide-strip {
  position: absolute;
  top: 0;
  left: calc(2 / 12 * 100%);
  width: calc(8 / 12 * 100%);
  height: 100%;
  overflow: visible;
}

@media (max-width: 767px) {
  .slide-strip {
    left: 5%;
    width: 90%;
  }
}

/* Swiper's own CSS sets overflow:hidden on .swiper — pierce it here */
.slide-strip :deep(.swiper) {
  overflow: visible;
}

/* In multi-slide mode the custom icon replaces the cursor, so suppress the
   figure's own cursor-zoom-in from showing beneath it (desktop only — no effect
   on touch). */
@media (min-width: 768px) {
  .multi :deep([data-slide-content="image"]),
  .multi :deep([data-slide-content="image"] *) {
    cursor: zoom-in !important;
  }
}
</style>
